
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Trash2, Plus, X, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import CustomDropdown from "@/components/admin/CustomDropdown";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  processing: "bg-aarsa-sage-100 text-aarsa-sage-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

export default function AdminOfflineSalesPage() {
  const { token } = useAuthStore();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const ordersPerPage = 30;

  useEffect(() => {
    if (token) {
      fetchOrders();
    }
  }, [token, currentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getOrders(token!, {
        page: currentPage,
        limit: ordersPerPage,
      });
      // Backend wraps response in: { success, message, data: { total, orders } }
      const wrappedResponse = response;
      let ordersList = [];
      let total = 0;
      
      if (wrappedResponse.data) {
        ordersList = wrappedResponse.data.orders || wrappedResponse.data || [];
        total = wrappedResponse.data.total || ordersList.length;
      } else if (wrappedResponse.orders) {
        ordersList = wrappedResponse.orders;
        total = wrappedResponse.total || ordersList.length;
      } else if (Array.isArray(wrappedResponse)) {
        ordersList = wrappedResponse;
        total = wrappedResponse.length;
      }
      
      // Filter for offline sales (payment_method = "Offline Sale")
      const offlineSales = ordersList.filter((order: any) => 
        order.payment?.payment_method === "Offline Sale" || 
        order.payment_method === "Offline Sale"
      );
      
      setOrders(offlineSales);
      setTotalOrders(offlineSales.length);
    } catch (error: any) {
      console.error("Error fetching offline sales:", error);
      toast.error(error.response?.data?.detail || "Failed to load offline sales");
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId: number, newStatus: string) => {
    if (!token) return;
    try {
      await adminApi.updateOrderStatus(orderId, newStatus, token);
      toast.success("Order status updated successfully");
      fetchOrders();
    } catch (error: any) {
      console.error("Error updating order status:", error);
      toast.error(error.response?.data?.detail || "Failed to update order status");
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder || !token) return;
    try {
      setDeleting(true);
      await adminApi.deleteOrder(selectedOrder.id, token);
      toast.success("Order deleted successfully");
      setShowDeleteModal(false);
      setSelectedOrder(null);
      // If current page becomes empty after deletion, go to previous page
      if (orders.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchOrders();
      }
    } catch (error: any) {
      console.error("Error deleting order:", error);
      toast.error(error.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-1" />
            <div className="h-3 w-48 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="w-1/2 h-9 bg-gray-200 rounded-full animate-pulse" />
          <div className="flex-shrink-0 h-9 w-32 bg-gray-200 rounded-lg animate-pulse ml-auto" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <th key={i} className="px-4 py-2.5">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const filteredOrders = orders.filter((order) =>
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${order.user?.first_name || ""} ${order.user?.last_name || ""}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.delivery_phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4">
      {/* Header, Search, and Create Order Button */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-xl font-bold font-playfair mb-0.5">Offline Sales</h1>
          <p className="text-xs text-gray-500">Manage offline sales and orders</p>
        </div>
        <div className="w-1/2 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search offline sales..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="flex-shrink-0 ml-auto">
          <Link
            to="/admin/offline-sales/new"
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Offline Sale
          </Link>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Selling Price
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    {searchTerm ? "No offline sales match your search" : "No offline sales found"}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-semibold text-sm text-gray-900">{order.order_number}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-sm text-gray-900">
                          {order.user?.first_name || ""} {order.user?.last_name || ""}
                        </p>
                        <p className="text-xs text-gray-500">{order.user?.email || "—"}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-sm text-gray-900">₹{order.total_amount?.toLocaleString() || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <CustomDropdown
                        value={order.status}
                        onChange={(newStatus) => handleStatusUpdate(order.id, newStatus)}
                        options={[
                          { value: "pending", label: "PENDING", className: "bg-yellow-100 text-yellow-700" },
                          { value: "confirmed", label: "CONFIRMED", className: "bg-blue-100 text-blue-700" },
                          { value: "processing", label: "PROCESSING", className: "bg-aarsa-sage-100 text-aarsa-sage-700" },
                          { value: "shipped", label: "SHIPPED", className: "bg-indigo-100 text-indigo-700" },
                          { value: "delivered", label: "DELIVERED", className: "bg-green-100 text-green-700" },
                          { value: "cancelled", label: "CANCELLED", className: "bg-red-100 text-red-700" },
                        ]}
                        className="w-32"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                          order.payment_status === "completed"
                            ? "bg-green-100 text-green-700"
                            : order.payment_status === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {order.payment_status?.toUpperCase() || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/offline-sales/${order.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-black text-white rounded-full text-xs font-medium hover:bg-gray-900 hover:scale-105 transition-all duration-200"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">View</span>
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDeleteModal(true);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-black text-white rounded-full text-xs font-medium hover:bg-gray-900 hover:scale-105 transition-all duration-200"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * ordersPerPage + 1} to{" "}
            {Math.min(currentPage * ordersPerPage, totalOrders)} of {totalOrders} offline sales
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedOrder(null);
            }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl p-5 max-w-md w-full"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Offline Sale</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete offline sale <span className="font-semibold">#{selectedOrder.order_number}</span>? This action cannot be undone and will also delete all related order items, timeline entries, and payment records.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedOrder(null);
                  }}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
